<template>
  <ValidationObserver ref="observer" rules="required" v-slot="{ handleSubmit, invalid }">
    <RightSideBar
      v-if="isOpen"
      @close="onClose"
      @back="handleBack"
      :loading="isLoading"
      @submit="handleSubmit(currentStep === 1 ? handleNext : onSubmit)"
      :submit="currentStep === 1 ? 'Next' : 'Save'"
      :cancel="currentStep === 2 ? 'Cancel' : 'Cancel'"
      :back-button="currentStep === 2"
      :close-button="isViewMode && currentStep === 2"
      button-class="bg-dynamicBackBtn text-white rounded"
      :disabled-button="isLoading || (currentStep === 1 && !isStep1Valid)"
      width="width:623px"
      padding="padding: 18px 20px"
    >
      <template v-slot:title>
        <h1 class="text-darkPurple font-bold text-lg">Leave Category</h1>
      </template>
      <template v-slot:default>
        <LeaveSettingsStep
          v-show="currentStep === 1"
          ref="leaveSettingsStep"
          :setting="setting"
          :active-tab-item="activeTabItem"
          :level-options="levelOptions"
          :pay-setting-options="paySettingOptions"
          :is-view-mode="isViewMode"
          :accrual-frequency-options="accrualFrequencyOptions"
          :monthly-accrue-date-options="monthlyAccrueDateOptions"
          :selected-levels="selectedLevels"
          @update:setting="handleSettingUpdate"
          @selected-levels="onSelectedLevels"
          @form-validity-changed="isStep1Valid = $event"
          :leave-category="activeTabItem"
        />

        <JobLevelEntitlementsStep
          v-show="currentStep === 2"
          :leave-levels="leaveLevels"
          :selected-levels="selectedLevels"
          :used-levels="usedLevels"
          :is-create-new="isCreateNew"
          :is-view-mode="isViewMode"
          :leave-category="activeTabItem"
          :employee-categories="employeeCategories"
          :category-exception="categoryException"
          :has-carry-forward="setting.hasCarryForward"
          @update:selectedLevels="selectedLevels = $event"
          @update:categoryException="categoryException = $event"
        />
      </template>
    </RightSideBar>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from "vee-validate"
import RightSideBar from "@/components/RightSideBar"
import LeaveSettingsStep from "./LeaveSettingsStep"
import JobLevelEntitlementsStep from "./JobLevelEntitlementsStep"

export default {
components: {
    ValidationObserver,
    RightSideBar,
    LeaveSettingsStep,
    JobLevelEntitlementsStep,
  },
  data() {
    return {
      isOpen: false,
      isLoading: true,
      isViewMode: false,
      setEligible: '',
      activeTabItem: null,
      levelOptions: [],
      paySettingOptions: [],
      leaveLevels: [],
      allOption: [],
      currentStep: 1,
      selectedLevels: [],
      usedLevels: [],
      isCreateNew: false,
      isStep1Valid: false,
      employeeCategories: [],
      categoryException: [],
      accrualFrequencyOptions: [
        { name: "Accrue daily", id: "daily" },
        { name: "Accrue monthly", id: "monthly" },
      ],
      monthlyAccrueDateOptions: [
        { name: "Last day of the month", id: "last_day" },
      ],

      setting: {
        level: [],
        status: null,
        eligibility: null,
        isEnabled: null,
        hasDelegation: null,
        paySettingId: null,
        allowanceType: null,
        hasPoration: null,
        hasCarryForward: null,
        includePublicHoliday: null,
        handoverNote: null,
        hasAccrual: false,
        accrualFrequency: null,
        dailyPostMonthlyType: null,
        hasLeaveOverdraw: false,
        leaveOverdrawDaysMax: null,
      }
    };
  },
  methods: {
    normalizeBoolean(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
      }
      return Boolean(value);
    },
    async onOpen({ setting = {}, activeTabItem, activeTableItems, isCreateNew }, actionType){
      try {
        this.isLoading = true
        this.isOpen = true
        this.currentStep = 1
        this.activeTabItem = activeTabItem
        this.isCreateNew = isCreateNew
        if (actionType === 'view') this.isViewMode = true
        if (actionType === 'edit') this.isViewMode = false
        if (Object.values(setting).length) {
          this.setting = {
            ...setting,
            level: [...setting.level],
            isEnabled: this.normalizeBoolean(setting.isEnabled),
            hasDelegation: this.normalizeBoolean(setting.hasDelegation),
            eligibility: this.normalizeBoolean(setting.eligibility),
            hasAccrual: this.normalizeBoolean(setting.hasAccrual) || false,
            accrualFrequency: setting.accrualFrequency || null,
            dailyPostMonthlyType: "last_day",
            hasLeaveOverdraw: this.normalizeBoolean(setting.hasLeaveOverdraw) || false,
            leaveOverdrawDaysMax: setting.leaveOverdrawDaysMax !== null && setting.leaveOverdrawDaysMax !== undefined ? setting.leaveOverdrawDaysMax : null,
            hasPoration: this.normalizeBoolean(setting.hasPoration),
            hasCarryForward: this.normalizeBoolean(setting.hasCarryForward),
            includePublicHoliday: this.normalizeBoolean(setting.includePublicHoliday),
            handoverNote: this.normalizeBoolean(setting.hasHandoverNote),
            allowanceType: setting.allowanceType || null,
            status: setting.status || null,
          }

          if (setting.levels && Array.isArray(setting.levels)) {
            this.selectedLevels = setting.levels.map(le => ({
              levelId: le.levelId,
              name: this.leaveLevels.find(l => l.id === le.levelId)?.name || `Level ${le.levelId}`,
              maximumApplicableDays: le.maximumApplicableDays !== null && le.maximumApplicableDays !== undefined ? le.maximumApplicableDays : null,
              carryOverDays: le.carryOverDays !== null && le.carryOverDays !== undefined ? le.carryOverDays : null,
              carryOverExpiryDate: le.carryOverExpiryDate ? new Date(le.carryOverExpiryDate) : null,
            }))
          }

          if (setting.categoryException && Array.isArray(setting.categoryException)) {
            this.categoryException = setting.categoryException.map(cat =>
              typeof cat === 'string' ? cat : (cat.id || cat.name)
            )
          } else {
            this.categoryException = []
          }
        }
        await this.getOrgtLevels(activeTableItems || [], isCreateNew)
        await this.getOrgPaySetting()
        await this.getExceptionCategories()

        if (this.selectedLevels.length === 0 && this.leaveLevels.length > 0) {
          this.selectedLevels = []
        }

        this.isLoading = false
      } catch (error) {
        this.$toasted.error('Failed to load leave settings', { duration: 3000 })
        this.isLoading = false
      }
    },
    handleSettingUpdate(newSetting) {
      const normalizedSetting = {
        ...newSetting,
        isEnabled: this.normalizeBoolean(newSetting.isEnabled),
        hasDelegation: this.normalizeBoolean(newSetting.hasDelegation),
        eligibility: this.normalizeBoolean(newSetting.eligibility),
        hasPoration: this.normalizeBoolean(newSetting.hasPoration),
        hasCarryForward: this.normalizeBoolean(newSetting.hasCarryForward),
        includePublicHoliday: this.normalizeBoolean(newSetting.includePublicHoliday),
        hasAccrual: this.normalizeBoolean(newSetting.hasAccrual) || false,
        hasLeaveOverdraw: this.normalizeBoolean(newSetting.hasLeaveOverdraw) || false,
      };
      this.setting = normalizedSetting;
    },
    handleNext() {
      this.$refs.observer.validate().then((success) => {
        if (success) {
          this.currentStep = 2
          if (this.setting.level && this.setting.level.length > 0) {
            this.initializeSelectedLevels()
          }
        }
      })
    },
    handleBack() {
      this.currentStep = 1
    },
    initializeSelectedLevels() {
      if (Array.isArray(this.setting.level)) {
        this.selectedLevels = this.setting.level
          .filter(level => level && level.id !== 'all')
          .map(level => {
            const levelData = this.leaveLevels.find(l => l.id === level.id || l.id === level)
            return {
              levelId: levelData?.id || level.id || level,
              name: levelData?.name || level.name || `Level ${level.id || level}`,
              maximumApplicableDays: level.maximumApplicableDays !== null && level.maximumApplicableDays !== undefined ? level.maximumApplicableDays : null,
              carryOverDays: level.carryOverDays !== null && level.carryOverDays !== undefined ? level.carryOverDays : null,
              carryOverExpiryDate: level.carryOverExpiryDate ? new Date(level.carryOverExpiryDate) : null,
            }
          })
      }
    },
    onSubmit(){

      const orgId = this.$AuthUser?.orgId;
      const categoryId = this.activeTabItem?.id;
      const levels = Array.isArray(this.selectedLevels) ? this.selectedLevels : [];

      if (!orgId) {
        this.$toasted.error('Organization ID is required', { duration: 3000 });
        return;
      }

      if (!categoryId) {
        this.$toasted.error('Category ID is required', { duration: 3000 });
        return;
      }

      if (!Array.isArray(levels) || levels.length === 0) {
        this.$toasted.error('At least one job level must be selected', { duration: 3000 });
        return;
      }

      const invalidCarryOver = levels.find(level => {
        if (!level.carryOverDays || level.carryOverDays === '' || level.carryOverDays === null) return false;
        if (!level.maximumApplicableDays || level.maximumApplicableDays === '' || level.maximumApplicableDays === null) return false;
        return Number(level.carryOverDays) > Number(level.maximumApplicableDays);
      });

      if (invalidCarryOver) {
        this.$toasted.error(`Carry over days cannot exceed entitlement days for ${invalidCarryOver.name}`, { duration: 5000 });
        return;
      }

      if (this.setting.hasLeaveOverdraw && this.setting.leaveOverdrawDaysMax) {
        const entitlementDays = levels
          .map(level => {
            const days = level.maximumApplicableDays;
            return days !== null && days !== undefined && days !== '' ? Number(days) : null;
          })
          .filter(days => days !== null);

        if (entitlementDays.length > 0) {
          const minEntitlement = Math.min(...entitlementDays);
          const overdrawDays = Number(this.setting.leaveOverdrawDaysMax);

          if (overdrawDays > minEntitlement) {
            this.$toasted.error(`Leave overdraw (${overdrawDays} days) cannot exceed the minimum entitlement days (${minEntitlement} days)`, { duration: 5000 });
            return;
          }
        }
      }

      this.isLoading = true;

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const payload = {
        levels: levels.map(level => ({
          levelId: level.levelId,
          maximumApplicableDays: level.maximumApplicableDays !== null && level.maximumApplicableDays !== undefined ? String(level.maximumApplicableDays) : null,
          carryOverDays: level.carryOverDays !== null && level.carryOverDays !== undefined ? String(level.carryOverDays) : null,
          carryOverExpiryDate: level.carryOverExpiryDate ? formatDate(level.carryOverExpiryDate) : null,
        })),
        categoryException: this.categoryException,
        status: this.setting.status,
        eligibility: this.setting.eligibility === true,
        isEnabled: this.setting.isEnabled === true,
        hasDelegation: this.setting.hasDelegation === true,
        paySettingId: this.setting.paySettingId || null,
        orgId: orgId,
        updatedBy: this.$AuthUser.id,
        categoryId: String(categoryId),
        allowanceType: this.setting.allowanceType || null,
        hasPoration: this.setting.hasPoration === true,
        hasCarryForward: this.setting.hasCarryForward === true,
        includePublicHoliday: this.setting.includePublicHoliday === true,
        hasHandoverNote: this.setting.handoverNote === true,
        hasAccrual: this.setting.hasAccrual === true,
        accrualFrequency: this.setting.accrualFrequency || null,
        dailyPostMonthlyType: this.setting.dailyPostMonthlyType || null,
        hasLeaveOverdraw: this.setting.hasLeaveOverdraw === true,
        leaveOverdrawDaysMax: this.setting.leaveOverdrawDaysMax !== null && this.setting.leaveOverdrawDaysMax !== undefined ? this.setting.leaveOverdrawDaysMax : null,
      }

      this.$_updateLevelSettings(payload)
        .then(({ data }) => {
          const message = data?.message || "Leave settings updated successfully";
          this.$toasted.success(message, { duration: 3000 });
          this.$emit('success', data);
          this.onClose();
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || error?.message || "Failed to update leave settings. Please try again.";
          this.$toasted.error(errorMessage, { duration: 3000 });
          this.$emit('error', error);
          this.isLoading = false;
        });
    },
    onSelectedLevels(items){
      if (items.length === this.levelOptions.length - 1) {
        this.levelOptions = [...this.allOption];
        this.setting.level = items.filter(item => item)
      } else {
        this.levelOptions = [...this.allOption];
      }
    },
    onClose(){
      this.isLoading = false
      this.isOpen = false
      this.isViewMode = false
      this.currentStep = 1
      this.selectedLevels = []
      this.categoryException = []
      Object.assign(this.$data.setting, this.$options.data().setting)
      this.$emit('close')
    },
    mergeLevel(activeTableItems){
      if (!activeTableItems || !Array.isArray(activeTableItems)) {
        return []
      }
      const mergedlevels = activeTableItems.flatMap(activeTableItem => activeTableItem.level || []);
      const uniquelevels = mergedlevels.filter(
        (level, index, self) =>
          index === self.findIndex(i => i.id === level.id)
      );

      return uniquelevels
    },
    async getOrgtLevels(activeTableItems, isCreateNew) {
      const uniquelevels = this.mergeLevel(activeTableItems)
      this.usedLevels = uniquelevels

      await this.$_getLevels().then(({ data }) => {
        this.leaveLevels = data.levels;

        const levels =  isCreateNew ? [
          ...data.levels.filter(level1 => uniquelevels.every(level2 => level2.id !== level1.id)),
          ...uniquelevels.filter(level2 => data.levels.every(level1 => level1.id !== level2.id))
        ] : [...data.levels];
        this.allOption = [{ name: 'All', id: 'all' }, ...levels]
        this.levelOptions = [...this.allOption]
      })
    },
    async getOrgPaySetting() {
      await this.$_getLeavePayItems().then(({ data }) => {
        this.paySettingOptions = data.paySettings.map(item => ({
          ...item, name: item.nameOnPayslip,
        }))
      })
    },
    async getExceptionCategories() {
      await this.$_getExceptionCategories().then(({ data }) => {
        this.employeeCategories = data.data || []
      }).catch((error) => {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to load employee categories";
        this.$toasted.error(errorMessage, { duration: 3000 });
        this.employeeCategories = []
      })
    },
  }
}
</script>
<style scoped>
.c-radio {
  width: 100% !important;
  display: flex !important;
  justify-content: flex-start !important;
  gap: 20px !important;
  font-size: 16px !important;
  color: #333333 !important;
}
::v-deep .applicable-days label {
  display: none !important;
}
.line{
  border: 1px solid #C2C7D6;
  width: 100%;
  height: 0;
  margin: 8px auto;
  border-radius: 10px;
  padding: 0;
}
</style>